import moment from "moment";
import "moment/locale/es";
import { dineroDisabledMask, round2Decimals } from "../Components/Items";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";
import { BloqueFirmasPdf } from "../Components/PdfComponents/BloqueFirmasPdf";

// import numeroALetra from "../Components/numeroALetra";

moment.locale("es");

const ordenCompraPdf = (values) => {
  const empresa = values.respuestaFactura.empresa;
  const mostrarClaveInterna = !empresa?.deshabilitarClaveInterna; 
  const mostrarDescUnidad = empresa?.imprimirDesUnidad ; // para mostrar la descripcion del producto
  const mostrarDescripcionInsumo = !!empresa?.imprimirDescripcionInsumoOC; // descripcion del insumo en cada fila
  const mostrarRegimen = !empresa?.deshabilitarRegimenFiscal; 
  const decimalesPrecioUnitarioPdf = Number.isFinite(Number(empresa?.decimalesPrecioUnitarioPdf)) ? Number(empresa.decimalesPrecioUnitarioPdf) : 2;
  const decimalesCantidadPdf = Number.isFinite(Number(empresa?.decimalesCantidadPdf)) ? Number(empresa.decimalesCantidadPdf) : 2;
  const formatCantidad = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return val;
    return num.toLocaleString('es-MX', { minimumFractionDigits: decimalesCantidadPdf, maximumFractionDigits: decimalesCantidadPdf });
  };

  // `decimalesEmpresa` es opcional (el select de configuración se puede limpiar) y
  // manda sobre el resto de los montos: importes, impuestos, descuentos y totales.
  // Si no se configura, esos montos siguen con 2 decimales, como antes.
  // Ojo con el 0: `dineroDisabledMask` lo trata como "no especificado" y cae a 2.
  const decimalesEmpresaConfig =
    Number.isFinite(Number(empresa?.decimalesEmpresa)) && Number(empresa.decimalesEmpresa) > 0
      ? Number(empresa.decimalesEmpresa)
      : null;
  const decimalesMonto = decimalesEmpresaConfig ?? 2;
  // El precio unitario conserva SU propia configuración ("Decimales Precio
  // Unitario"): `decimalesEmpresa` no lo toca. Igual que la Cantidad, que sigue
  // con `decimalesCantidadPdf`.
  const decimalesPU = decimalesPrecioUnitarioPdf;
  const ajustarAnchos = decimalesEmpresaConfig !== null;

  const widthClaveInt = 10;
  const widthIEPS = 8;

  let widthExtraDescripcion = 0;

  if(!mostrarClaveInterna) widthExtraDescripcion += widthClaveInt;
  if (!empresa.imprimirIEPS) widthExtraDescripcion += widthIEPS;

  const dataSat = values.respuestaFactura.dataSat;

  const impuestosDeConcepto = (valueConcepto) => {
    let iva = 0;
    let ieps = 0;
    valueConcepto.Impuestos?.Traslados?.forEach((valueImpuesto) => {
      if (valueImpuesto.Impuesto === "002" && valueImpuesto.Importe) {
        iva += valueImpuesto.Importe;
      }
      if (valueImpuesto.Impuesto === "003" && valueImpuesto.Importe) {
        ieps += valueImpuesto.Importe;
      }
    });
    return { iva, ieps };
  };

  // Con más decimales el monto deja de caber en su columna y pdfmake lo parte en
  // dos renglones, dejando el "$" solo arriba. Se mide el monto más largo de cada
  // columna de dinero y, si no cabe, la diferencia se le cobra a Descripción.
  // Sólo corre cuando hay configuración: así un PDF que hoy se ve bien no cambia.
  const FONT_TABLA = 6;       // style textoTablaChico
  const HOLGURA = 1.02;       // colchón: la medición por caracter es aproximada
  const DESCRIPCION_MIN = 18; // % mínimo que conserva Descripción
  const COLUMN_GAP_DESCUENTO = 4; // separación entre el % y el $ del descuento

  // pdfmake reparte los % sobre el ancho ya sin padding:
  // availableWidth = pagina - margenes - offsets.total (padding de celdas + lineas).
  const numColumnasTabla = 8 + (mostrarClaveInterna ? 1 : 0) + (empresa.imprimirIEPS ? 1 : 0);
  const ANCHO_COLUMNAS_PT = 612 - 80 - (numColumnasTabla * 8 + numColumnasTabla + 1);

  // Ancho aproximado en puntos para Roboto, la fuente por defecto de pdfmake.
  // Los factores salen de las métricas reales del TTF (unitsPerEm 2048).
  const anchoTextoPt = (texto, fontSize) => {
    let em = 0;
    for (const caracter of String(texto ?? "")) {
      if (caracter === " ") em += 0.248;
      else if (caracter === ",") em += 0.197;
      else if (caracter === "." ) em += 0.264;
      else if (caracter === "-") em += 0.276;
      else if (caracter === "%") em += 0.732;
      else em += 0.562; // dígitos y "$"
    }
    return em * fontSize;
  };

  const pctDeAncho = (pt) => (pt / ANCHO_COLUMNAS_PT) * 100 * HOLGURA;
  const anchoNecesarioPct = (textos, fontSize = FONT_TABLA) =>
    pctDeAncho(textos.reduce((max, t) => Math.max(max, anchoTextoPt(t, fontSize)), 0));

  let anchoPU = 8;
  let anchoIVA = 8;
  let anchoIEPS = widthIEPS;
  let anchoImporte = 10;
  let anchoDescripcion = 26 + widthExtraDescripcion;
  // 1 = las columnas alcanzaron el ancho que pedían; < 1 = Descripción llegó a su
  // mínimo y no pudo pagar todo, así que el ensanche se reparte a escala.
  let factorAncho = 1;

  if (ajustarAnchos) {
    const conceptosMedidos = dataSat.Conceptos || [];
    const impuestosPorConcepto = conceptosMedidos.map(impuestosDeConcepto);

    // La celda de P/U no es sólo el precio: cuando hay descuento le cuelga un
    // renglón con el % a la izquierda y el descuento unitario a la derecha,
    // separados por columnGap. Si no se mide, ese renglón se sale.
    const anchoFilaDescuentoPU = (c) => {
      if (isNaN(c.Descuento) || !(Number(c.Descuento) > 0)) return 0;
      const pct = round2Decimals(
        (Number(c.Descuento) * 100) /
        (Number(c.Cantidad || 1) * Number(c.ValorUnitario || 1))
      ) + "%";
      const monto = dineroDisabledMask(Number(c.Descuento) / Number(c.Cantidad || 1), decimalesMonto);
      return anchoTextoPt(pct, 5) + COLUMN_GAP_DESCUENTO + anchoTextoPt(monto, 5);
    };

    // El subtotal por fecha es la suma de los Importes del grupo, o sea el número
    // más grande de esa columna, y además va en negrita (~1.2% más ancho).
    const subtotalesPorFecha = [];
    if (conceptosMedidos.length > 0) {
      const porFechaMedida = {};
      conceptosMedidos.forEach((c) => {
        const clave = c.FechaEntrega ? String(c.FechaEntrega) : "sin";
        porFechaMedida[clave] = (porFechaMedida[clave] || 0) + (Number(c.Importe) || 0) - (Number(c.Descuento) || 0);
      });
      Object.values(porFechaMedida).forEach((s) => subtotalesPorFecha.push(dineroDisabledMask(s, decimalesMonto)));
    }

    const incPU = Math.max(
      0,
      anchoNecesarioPct(conceptosMedidos.map((c) => dineroDisabledMask(c.ValorUnitario, decimalesPU))) - anchoPU,
      pctDeAncho(conceptosMedidos.reduce((max, c) => Math.max(max, anchoFilaDescuentoPU(c)), 0)) - anchoPU
    );
    const incIVA = Math.max(0, anchoNecesarioPct(impuestosPorConcepto.map((i) => dineroDisabledMask(i.iva, decimalesMonto))) - anchoIVA);
    const incIEPS = empresa.imprimirIEPS
      ? Math.max(0, anchoNecesarioPct(impuestosPorConcepto.map((i) => dineroDisabledMask(i.ieps, decimalesMonto))) - anchoIEPS)
      : 0;
    const incImporte = Math.max(
      0,
      anchoNecesarioPct(conceptosMedidos.map((c) => dineroDisabledMask(c.Importe, decimalesMonto))) - anchoImporte,
      pctDeAncho(subtotalesPorFecha.reduce((max, t) => Math.max(max, anchoTextoPt(t, FONT_TABLA) * 1.012), 0)) - anchoImporte
    );

    const extra = incPU + incIVA + incIEPS + incImporte;
    const disponible = Math.max(0, anchoDescripcion - DESCRIPCION_MIN);
    // Sin esto los anchos sumarían más de 100% y la tabla se saldría de la hoja.
    if (extra > disponible) factorAncho = disponible / extra;

    anchoPU += incPU * factorAncho;
    anchoIVA += incIVA * factorAncho;
    anchoIEPS += incIEPS * factorAncho;
    anchoImporte += incImporte * factorAncho;
    anchoDescripcion -= extra * factorAncho;
  }

  // Si no se pudo ensanchar lo suficiente, se achica la letra en la misma
  // proporción: el monto sigue cabiendo en un solo renglón sin invadir la celda
  // vecina. Con factorAncho === 1 no se toca nada y todo queda como estaba.
  // Piso de 4: con noWrap y alineación a la derecha, un monto que no cabe se
  // dibuja encima de la columna de al lado (que también lleva números), así que
  // se prefiere letra chica antes que cifras encimadas. Sólo pasa con montos de
  // 9 dígitos y 6 decimales a la vez.
  const fontMontoTabla = factorAncho < 1 ? Math.max(4, FONT_TABLA * factorAncho) : null;
  const fontMontoDescuento = factorAncho < 1 ? Math.max(3.5, 5 * factorAncho) : null;
  const estiloMonto = fontMontoTabla ? { fontSize: fontMontoTabla } : {};
  const estiloMontoDescuento = fontMontoDescuento ? { fontSize: fontMontoDescuento } : {};
  // Sólo se emite noWrap cuando hay ensanche; sin configuración el documento
  // queda sin esa llave, idéntico al de hoy.
  const sinCorte = ajustarAnchos ? { noWrap: true } : {};

  const tableWidths = [
            "2%", //Número de item
            '8%', //Cantidad
             ...(mostrarClaveInterna ? [`${widthClaveInt}%`] : []), //Clave Interna
            '10%', //Clave Proveedor
            '10%', //Unidad
            `${anchoDescripcion.toFixed(2)}%`,  //Descripción
            `${anchoPU.toFixed(2)}%`, //P/U
            `${anchoIVA.toFixed(2)}%`, //IVA
            ...(empresa.imprimirIEPS ? [`${anchoIEPS.toFixed(2)}%`] : []), //IEPS
            `${anchoImporte.toFixed(2)}%`, //IMPORTE
          ]

  // Complemento imprimirDescripcionInsumoOC: debajo de la descripción del proveedor
  // va la descripción del insumo en el sistema, si es distinta.
  const textoDescripcionConInsumo = (valueConcepto) => {
    const descripcionInsumo = String(valueConcepto.DescripcionInsumo || "").trim();
    const repiteDescripcion =
      descripcionInsumo.toLowerCase() === String(valueConcepto.Descripcion || "").trim().toLowerCase();
    return [
      valueConcepto.Descripcion,
      ...(descripcionInsumo && !repiteDescripcion
        ? [{ text: "\nInsumo: " + descripcionInsumo, color: "#555" }]
        : []),
      ...(valueConcepto.Observaciones ? ["\n" + valueConcepto.Observaciones] : []),
    ];
  };

  let numeroConcepto = 0;
  const filaConcepto = (valueConcepto) => {
    numeroConcepto += 1;
    const { iva, ieps } = impuestosDeConcepto(valueConcepto);

    return [
      { style: "textoTablaChico", text: numeroConcepto, alignment: "center" },//cantidad
      { style: "textoTablaChico", text: formatCantidad(valueConcepto.Cantidad), alignment: "right" },//cantidad
      ...(mostrarClaveInterna ? [{
        text: valueConcepto.ClaveInt,
        style: "textoTablaChico",   
        alignment: "center",
      }] : []),
      {
        text: valueConcepto.ClaveProv, //clave provedor
        style: "textoTablaChico",
        alignment: "center",
      },
      {
        text: (mostrarDescUnidad  &&  valueConcepto.descUnidad) ? valueConcepto.descUnidad : valueConcepto.ClaveUnidad, //clave unidad
        style: "textoTablaChico",
        alignment: "center",
      },
      {
        text: mostrarDescripcionInsumo
          ? textoDescripcionConInsumo(valueConcepto)
          : valueConcepto.Descripcion + (valueConcepto.Observaciones ? ("\n" + valueConcepto.Observaciones) : ""),    //descripcion
        style: "textoTablaChico",
        alignment: "left",
      },
      {
        stack: [
          {
            text: dineroDisabledMask(valueConcepto.ValorUnitario, decimalesPU), //valor unitario),
            style: "textoTablaChico",
            alignment: "right",
            ...sinCorte,
            ...estiloMonto,
          },

          ...(
            !isNaN(valueConcepto.Descuento) && Number(valueConcepto.Descuento) > 0
              ? [{
                  columns: [
                    {
                      text:
                        round2Decimals(
                          (Number(valueConcepto.Descuento) * 100) /
                          (Number(valueConcepto.Cantidad || 1) *
                          Number(valueConcepto.ValorUnitario || 1))
                        ) + "%",
                      width: "*",
                      alignment: "left",
                      style: "descuentoPartida",
                      noWrap: true,
                    },
                    {
                      text: dineroDisabledMask(
                          Number(valueConcepto.Descuento) /
                          Number(valueConcepto.Cantidad || 1),
                          decimalesMonto
                        ),
                      width: "auto",
                      style: "descuentoPartida",
                      ...sinCorte,
                      ...estiloMontoDescuento,
                    }
                  ],
                  columnGap: COLUMN_GAP_DESCUENTO,
                  margin: [0, 2, 0, 0]
                }]
              : []
          )
        ]
      },
      {
        text: dineroDisabledMask(iva, decimalesMonto),
        style: "textoTablaChico",
        alignment: "right",
        ...sinCorte,
        ...estiloMonto,
      },
      ...(empresa.imprimirIEPS
        ? [
            {
              text: dineroDisabledMask(ieps, decimalesMonto),
              style: "textoTablaChico",
              alignment: "right",
              ...sinCorte,
              ...estiloMonto,
            },
          ]
        : []),
      {
        stack: [
          {
            text: dineroDisabledMask(valueConcepto.Importe, decimalesMonto),
            style: "textoTablaChico",
            alignment: "right",
            ...sinCorte,
            ...estiloMonto,
          },
          ...(
          !isNaN(valueConcepto.Descuento) && Number(valueConcepto.Descuento) > 0
            ? [{
                text: "- " + dineroDisabledMask(Number(valueConcepto.Descuento), decimalesMonto),
                style: "descuentoPartida",
                alignment: "right",
                margin: [0, 2, 0, 0],
                ...sinCorte,
                ...estiloMontoDescuento,
              }]
            : []
          )
        ]
      }
    ];
  };

  const crearFilaEncabezado = () => [
    { text: '', style: 'tituloTabla' },
    { text: 'Cantidad', style: 'tituloTabla' },
    ...(mostrarClaveInterna ? [{ text: 'Clave Int.', style: 'tituloTabla' }] : []),
    { text: 'Clave Prov.', style: 'tituloTabla' },
    { text: 'Unidad', style: 'tituloTabla' },
    { text: 'Descripción', style: 'tituloTabla' },
    { text: 'P/U', style: 'tituloTabla' },
    { text: 'IVA', style: 'tituloTabla' },
    ...(empresa.imprimirIEPS ? [{ text: "IEPS", style: "tituloTabla" }] : []),
    { text: 'Importe', style: 'tituloTabla' }
  ];

  const tablaConceptosLayout = {
    fillColor: (rowIndex) => rowIndex % 2 === 0 ? "#f5f5f5" : "#ffffff",
    hLineWidth: (i, node) => (i === 0 || i === node.table.body.length) ? 1 : 1,
    vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length) ? 1 : 1,
    hLineColor: () => "#adaaaa",
    vLineColor: () => "#adaaaa",
    hLineStyle: (i, node) => (i === 0 || i === node.table.body.length) ? null : { dash: { length: 2, space: 1 } },
    vLineStyle: (i, node) => (i === 0 || i === node.table.widths.length) ? null : { dash: { length: 2 } },
  };

  const porFecha = {};
  dataSat.Conceptos.forEach((valueConcepto) => {
    const fechaKey = valueConcepto.FechaEntrega
      ? moment(valueConcepto.FechaEntrega).locale('es').format('L')
      : 'Sin fecha de entrega';
    if (!porFecha[fechaKey]) porFecha[fechaKey] = [];
    porFecha[fechaKey].push(valueConcepto);
  });

  const fechasOrdenadas = Object.keys(porFecha).sort((a, b) => {
    if (a === 'Sin fecha de entrega') return 1;
    if (b === 'Sin fecha de entrega') return -1;
    return moment(a, 'L').valueOf() - moment(b, 'L').valueOf();
  });

  const separarPorFecha = fechasOrdenadas.length > 1;

  const numColumnas = tableWidths.length;
  const indexDescripcion = mostrarClaveInterna ? 5 : 4;
  const filaSubtotalFecha = (conceptosFecha) => {
    const subtotalFecha = conceptosFecha.reduce(
      (suma, c) => suma + (Number(c.Importe) || 0) - (Number(c.Descuento) || 0),
      0
    );
    return Array.from({ length: numColumnas }, (_, i) => {
      if (i === indexDescripcion) return { text: 'Subtotal:', style: 'totalChico', alignment: 'right' };
      if (i === numColumnas - 1) return { text: dineroDisabledMask(subtotalFecha, decimalesMonto), style: 'totalChico', alignment: 'right', ...sinCorte, ...estiloMonto };
      return { text: '', style: 'totalChico' };
    });
  };

  const tablasConceptos = fechasOrdenadas.flatMap((fechaKey) => {
    const conceptosFecha = porFecha[fechaKey];
    return [
      ...(separarPorFecha ? [{ text: `Fecha de Entrega: ${fechaKey}`, style: 'subheader', margin: [0, 5, 0, 2] }] : []),
      {
        style: 'tableExample',
        table: {
          headerRows: 1,
          widths: tableWidths,
          layout: { defaultBorder: true },
          body: [
            crearFilaEncabezado(),
            ...conceptosFecha.map((valueConcepto) => filaConcepto(valueConcepto)),
            ...(separarPorFecha ? [filaSubtotalFecha(conceptosFecha)] : []),
          ]
        },
        layout: tablaConceptosLayout,
      }
    ];
  });

  const nuevoArrayImpFL = Array.isArray(dataSat.Impuestos.Traslados) ?
    [...dataSat.Impuestos.Traslados, ...(dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["implocal:TrasladosLocales"] || [])]
    :
    [...(dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["implocal:TrasladosLocales"] || [])]

  // Definir orden personalizado
  const ordenImpuestos = ["IVA 0%", "IVA 16%", "IEPS 6%", "IEPS 7%", "IEPS 9%"];

  // Ordenar el array antes del map
  const nuevoArrayImpFLOdenado = [...nuevoArrayImpFL].sort((a, b) => {
    const nombreA = a.Impuesto === "002"
      ? `IVA ${round2Decimals(a.TasaOCuota * 100)}%`
      : a.Impuesto === "003"
        ? `IEPS ${round2Decimals(a.TasaOCuota * 100)}%`
        : a["@ImpLocTrasladado"];
    const nombreB = b.Impuesto === "002"
      ? `IVA ${round2Decimals(b.TasaOCuota * 100)}%`
      : b.Impuesto === "003"
        ? `IEPS ${round2Decimals(b.TasaOCuota * 100)}%`
        : b["@ImpLocTrasladado"];

    return ordenImpuestos.indexOf(nombreA) - ordenImpuestos.indexOf(nombreB);
  });

  const impuestosTotales = nuevoArrayImpFLOdenado.map(
    (valueImpuesto, indexImpuesto) => {
      let nombre = "";
      if (valueImpuesto.Impuesto === "002") {
        nombre = "IVA";
      } else if (valueImpuesto.Impuesto === "003") {
        nombre = "IEPS";
      } else {
        nombre = valueImpuesto["@ImpLocTrasladado"];
      }

      if (valueImpuesto.TipoFactor === "Tasa") {
        nombre = nombre + " " + round2Decimals(valueImpuesto.TasaOCuota * 100) + "%";
      } else if (valueImpuesto.TipoFactor === "Cuota") {
        nombre = nombre + " $" + round2Decimals(valueImpuesto.TasaOCuota);
      }

      let border = indexImpuesto + 1 === nuevoArrayImpFLOdenado.length;
      return [
        {
          border: [true, false, false, border],
          text: nombre,
          style: "subheader",
        },
        {
          border: [false, false, true, border],
          text: dineroDisabledMask(valueImpuesto.Importe || valueImpuesto["@Importe"] || 0, decimalesMonto),
          style: "textoChico",
          alignment: "right",
          noWrap: true,
        },
      ];
    }
  );

  const nuevoArrayRetFL = Array.isArray(dataSat.Impuestos.Retenciones) ?
    [...dataSat.Impuestos.Retenciones, ...(dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["implocal:RetencionesLocales"] || [])]
    :
    [...(dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["implocal:RetencionesLocales"] || [])]

  const retencionesTotales = nuevoArrayRetFL.map(
    (valueRetencion, indexRetencion) => {
      let nombre = "Retención ";
      if (valueRetencion.Impuesto === "001") {
        nombre = nombre + "ISR";
      } else if (valueRetencion.Impuesto === "002") {
        nombre = nombre + "IVA";
      } else {
        nombre = valueRetencion["@ImpLocRetenido"];
      }

      let border = indexRetencion + 1 === nuevoArrayRetFL.length;
      return [
        {
          border: [true, false, false, border],
          text: nombre,
          style: "subheader",
        },
        {
          border: [false, false, true, border],
          text: dineroDisabledMask(valueRetencion.Importe || valueRetencion["@Importe"] || 0, decimalesMonto),
          style: "textoChico",
          alignment: "right",
          noWrap: true,
        },
      ];
    }
  );

  const totalImpFL = dataSat.Impuestos.TotalImpuestosTrasladados + (dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["@TotaldeTraslados"] || 0) || 0;
  const totalRetFL = dataSat.Impuestos.TotalImpuestosRetenidos + (dataSat?.Complemento?.Any?.[0]?.["implocal:ImpuestosLocales"]?.["@TotaldeRetenciones"] || 0) || 0;

  let cancelado = {};
  if (values.estado === 0) {
    cancelado = { watermark: { text: 'CANCELADO', color: 'GRAY', opacity: 0.3, bold: true, italics: false, angle: -50, fontSize: 110 } };
  }
  let observaciones = {};
  if (values.imprimirObservaciones && values.observaciones) {
    observaciones = {
      table: {
        widths: ['*'],
        body: [
          [
            {
              border: [true, true, true, false],
              text: "Observaciones:",
              margin: [0, 0, 0, 5],
              style: "header",
            },
          ],
          [
            {
              border: [true, false, true, true],
              text: values.observaciones,
              margin: [0, 0, 0, 5],
              style: "textoChico",
            },
          ],
        ],
      },
      layout: {
          defaultBorder: false,
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 1 : 1;
          },
          vLineWidth: function (i, node) {
            return i === 0 || i === node.table.widths.length ? 1 : 1;
          },
          hLineColor: function (i, node) {
            return "#adaaaa";
          },
          vLineColor: function (i, node) {
            return "#adaaaa";
          },
          hLineStyle: function (i, node) {
            if (i === 0 || i === node.table.body.length) {
              return null;
            }
            return { dash: { length: 2, space: 1 } };
          },
          vLineStyle: function (i, node) {
            if (i === 0 || i === node.table.widths.length) {
              return null;
            }
            return { dash: { length: 2 } };
          },
        },
    };
  }

  const arraySiHayRetenciones = totalRetFL > 0 ? [
    //   [
    //   {
    //     border: [true, false, false, false],
    //     text: "Retenciones:",
    //     style: "subheader",
    //   },
    //   {
    //     border: [false, false, true, false],
    //     text: dineroDisabledMask(totalRetFL),
    //     style: "textoChico",
    //     alignment: "right",
    //   },
    // ],
    [
      {
        border: [true, false, false, true],
        text: "TOTAL PAGO:",
        style: "header",
        fillColor: "#e8e8e8",
      },
      {
        border: [false, false, true, true],
        text: dineroDisabledMask(dataSat.Total, decimalesMonto),
        style: "header",
        fillColor: "#e8e8e8",
        alignment: "right",
        noWrap: true,
      },
    ]] : [];

  const total = dataSat.SubTotal - dataSat.Descuento + totalImpFL;
  var dd = {
    ...cancelado,
    content: [
      {
        style: 'tableExample',
        table: {
          widths: ['18%', '52%', '30%'],
          layout: { defaultBorder: false },
          body: [
            [
              ...HeaderEmpresaCard(empresa, mostrarRegimen,false, 
              {
                border: [false, false, false, false],
                table: {
                  headerRows: 1,
                  widths: ['100%'],
                  body: [
                    [
                      {
                        text: 'N° Orden:',
                        style: 'subheader',
                        alignment: 'center'
                      }
                    ],
                    [
                      {
                        text: (dataSat.Serie ? dataSat.Serie : 'OC') + ' - ' + dataSat.Folio,
                        style: 'textoChico',
                        alignment: 'center'
                      }
                    ],
                    [
                      {
                        text: 'Moneda:',
                        style: 'subheader',
                        alignment: 'center'
                      }
                    ],
                    [
                      {
                        text: dataSat.Moneda,
                        style: 'textoChico',
                        alignment: 'center'
                      }
                    ],
                    [
                      {
                        text: 'Proyecto:',
                        style: 'subheader',
                        alignment: 'center'
                      }
                    ],
                    [
                      {
                        text: dataSat.ProyectoNombre ? dataSat.ProyectoNombre : '',
                        style: 'textoChico',
                        alignment: 'center'
                      }
                    ],
                  ],
                },
                layout: 'noBorders'
              })
            ]
          ]
        }
      },
      { text: 'Orden de Compra N° ' + (dataSat.Serie ? dataSat.Serie : 'OC') + ' - ' + dataSat.Folio, style: 'tableHeaderIzquierda' },
      {
        style: 'tableExample',
        table: {
          widths: ['50%', '50%'],
          layout: { defaultBorder: false },
          body: [
            [
              {
                table: {
                  widths: ['30%', '70%'],
                  layout: { defaultBorder: false },
                  body: [
                    [
                      {
                        text: 'Nombre del proveedor :',
                        style: 'subheader'
                      },
                      { text: dataSat.Receptor.Nombre, style: 'textoChico' }
                    ],
                    ...(dataSat?.Receptor?.Rfc
                      ? [[
                          { text: 'RFC:', style: 'subheader' },
                          { text: dataSat.Receptor.Rfc, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.Receptor?.Direccion
                      ? [[
                          { text: 'Dirección:', style: 'subheader' },
                          { text: dataSat.Receptor.Direccion, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.Receptor?.Telefono
                      ? [[
                          { text: 'Teléfono:', style: 'subheader' },
                          { text: String(dataSat.Receptor.Telefono), style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.Receptor?.Correo
                      ? [[
                          { text: 'Correo:', style: 'subheader' },
                          { text: dataSat.Receptor.Correo, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.Receptor?.ResidenciaFiscal
                      ? [[
                          { text: 'Residencia Fiscal:', style: 'subheader' },
                          { text: dataSat.Receptor.ResidenciaFiscal, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.Receptor?.NumRegIdTrib
                      ? [[
                          { text: 'Num. Reg. Id. Trib.:', style: 'subheader' },
                          { text: dataSat.Receptor.NumRegIdTrib, style: 'textoChico' },
                        ]]
                      : []),
                    [
                      { text: 'Fecha:', style: 'subheader' },
                      {
                        text: moment(dataSat.Fecha).locale("es").format("LL"),
                        style: 'textoChico'
                      }
                    ],
                    [
                      {
                        text: 'Lugar de expedicion:',
                        style: 'subheader'
                      },
                      { text: dataSat.LugarExpedicion, style: 'textoChico' }
                    ],
                  ]
                },
                layout: 'noBorders'
              },
              {
                table: {
                  widths: ['30%', '70%'],
                  layout: { defaultBorder: false },
                  body: [
                    ...(dataSat?.destino
                      ? [[
                          { text: "Destino:", style: "subheader" },
                          {
                            text: dataSat?.destino,
                            style: "textoChico",
                          },
                        ]]
                      : []),
                      ...(dataSat?.embarque
                      ? [[
                          { text: "Embarque:", style: "subheader" },
                          {
                            text: dataSat?.embarque,
                            style: "textoChico",
                          },
                        ]]
                      : []),
                    [
                      {
                        text: 'Direccion de Entrega :',
                        style: 'subheader'
                      },
                      { text: dataSat?.DireccionEntrega ? dataSat.DireccionEntrega : '', style: 'textoChico' }
                    ],
                    ...(dataSat?.atencion
                      ? [[
                      { text: "Atención a: ", style: "subheader" },
                      { text: dataSat?.atencion, style: "textoChico" },
                      ]]
                      : []),
                    [
                      { text: 'Fecha de Entrega:', style: 'subheader' },
                      {
                        text: dataSat?.FechaEntrega ? moment(dataSat.FechaEntrega).locale("es").format("LL") : 'S/F',
                        style: 'textoChico'
                      }
                    ],
                    ...(dataSat.cotizacionOC?.trim()
                      ? [[
                        { text: 'Cotización:', style: 'subheader' },
                        { text: dataSat.cotizacionOC, style: 'textoChico' }
                      ]]
                      : []),
                    ...(dataSat?.usoDeCFDI
                      ? [[
                          { text: 'Uso de CFDI:', style: 'subheader' },
                          { text: dataSat.usoDeCFDI, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.formaDePago
                      ? [[
                          { text: 'Forma de Pago:', style: 'subheader' },
                          { text: dataSat.formaDePago, style: 'textoChico' },
                        ]]
                      : []),
                    ...(dataSat?.metodoPago
                      ? [[
                          { text: 'Método de Pago:', style: 'subheader' },
                          { text: dataSat.metodoPago, style: 'textoChico' },
                        ]]
                      : []),
                  ]
                },
                layout: 'noBorders'
              },
            ]
          ]
        },
        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === node.table.body.length ? 1 : 1;
          },
          vLineWidth: function (i, node) {
            return i === 0 || i === node.table.widths.length ? 1 : 1;
          },
          hLineColor: function (i, node) {
            return "#adaaaa";
          },
          vLineColor: function (i, node) {
            return "#adaaaa";
          },
          hLineStyle: function (i, node) {
            return null; // Elimina las líneas horizontales punteadas
          },
          vLineStyle: function (i, node) {
            return null; // Elimina las líneas verticales punteadas
          },
        }
      },
      ...tablasConceptos,
      {
        style: 'tablaSinMargen',
        table: {
          widths: ['70%', '30%'],
          layout: { defaultBorder: true },
          body: [
            [
              {
                // table: {
                //   widths: ['30%', '70%'],
                //   layout: { defaultBorder: false },
                //   body: [
                //     [
                //       {
                //         border: [true, true, false, false],
                //         text: "Cantidad con letra:",
                //         style: "subheader",
                //       },
                //       {
                //         border: [false, true, true, false],
                //         text: "**( " + numeroALetra(dataSat.Total, dataSat.Moneda) + " )**",
                //         style: "textoChico",
                //         fontSize: 7,
                //       },
                //     ],
                //     [
                //       {
                //         border: [true, true, false, false],
                //         text: "Banco:",
                //         style: "subheader",
                //       },
                //       {
                //         border: [false, true, true, false],
                //         text: empresa.banco ? empresa.banco : "",
                //         style: "textoChico",
                //       },
                //     ],
                //     [
                //       {
                //         border: [true, false, false, false],
                //         text: "Cuenta:",
                //         style: "subheader",
                //       },
                //       {
                //         border: [false, false, true, false],
                //         text: empresa.cuentaBanco ? empresa.cuentaBanco : "",
                //         style: "textoChico",
                //       },
                //     ],
                //     [
                //       {
                //         border: [true, false, false, true],
                //         text: "Cuenta CLABE:",
                //         style: "subheader",
                //       },
                //       {
                //         border: [false, false, true, true],
                //         text: empresa.cuentaClabe ? empresa.cuentaClabe : "",
                //         style: "textoChico",
                //       },
                //     ],
                //   ]
                // },
                // layout: {
                //   hLineWidth: function (i, node) {
                //     return i === 0 || i === node.table.body.length ? 1 : 1;
                //   },
                //   vLineWidth: function (i, node) {
                //     return i === 0 || i === node.table.widths.length ? 1 : 1;
                //   },
                //   hLineColor: function (i, node) {
                //     return "#adaaaa";
                //   },
                //   vLineColor: function (i, node) {
                //     return "#adaaaa";
                //   },
                //   hLineStyle: function (i, node) {
                //     if (i === 0 || i === node.table.body.length) {
                //       return null;
                //     }
                //     return { dash: { length: 2, space: 1 } };
                //   },
                //   vLineStyle: function (i, node) {
                //     if (i === 0 || i === node.table.widths.length) {
                //       return null;
                //     }
                //     return { dash: { length: 2 } };
                //   },
                // }
              },
              {
                table: {
                  widths: ['35%', '65%'],
                  layout: { defaultBorder: false },
                  body: [
                    [
                      {
                        border: [true, true, false, false],
                        text: 'Subtotal:',
                        style: 'subheader'
                      },
                      {
                        border: [false, true, true, false],
                        text: dineroDisabledMask(dataSat.SubTotal, decimalesMonto),
                        style: 'textoChico',
                        alignment: 'right',
                        noWrap: true
                      }
                    ],
                    [
                      {
                        border: [true, false, false, false],
                        text: 'Descuento:',
                        style: 'subheader'
                      },
                      {
                        border: [false, false, true, false],
                        text: "- "+dineroDisabledMask(dataSat.Descuento, decimalesMonto),
                        style: 'textoChico',
                        alignment: 'right',
                        noWrap: true
                      }
                    ],
                    ...impuestosTotales,
                    [
                      {
                        border: [true, false, false, true],
                        text: 'TOTAL:',
                        style: 'header',
                        fillColor: '#e8e8e8'
                      },
                      {
                        border: [false, false, true, true],
                        text: dineroDisabledMask(total, decimalesMonto),
                        style: 'header',
                        fillColor: '#e8e8e8',
                        alignment: 'right',
                        noWrap: true
                      }
                    ],
                    ...retencionesTotales,
                    ...arraySiHayRetenciones,
                  ]
                },
                layout: {
                  hLineWidth: function (i, node) {
                    return i === 0 || i === node.table.body.length ? 1 : 1;
                  },
                  vLineWidth: function (i, node) {
                    return i === 0 || i === node.table.widths.length ? 1 : 1;
                  },
                  hLineColor: function (i, node) {
                    return "#adaaaa";
                  },
                  vLineColor: function (i, node) {
                    return "#adaaaa";
                  },
                  hLineStyle: function (i, node) {
                    if (i === 0 || i === node.table.body.length) {
                      return null;
                    }
                    return { dash: { length: 2, space: 1 } };
                  },
                  vLineStyle: function (i, node) {
                    if (i === 0 || i === node.table.widths.length) {
                      return null;
                    }
                    return { dash: { length: 2 } };
                  },
                }
              }
            ]
          ]
        },
        layout: 'noBorders'
      },
      ...(observaciones ? [
        observaciones,
        {
          text: empresa?.observacionesPdf ? empresa.observacionesPdf : '',
          style: 'textoChico',
          margin: [0, 10, 0, 0]
        },
      ] : []),
      ...(empresa?.camposFirmaOC
        ? [BloqueFirmasPdf([
            { titulo: "Elaboró", nombre: dataSat?.nombreCreador || values?.nombreCreador || "", firma: dataSat?.firmaCreador || null },
            { titulo: "Autorizó", nombre: dataSat?.nombreAprobador || values?.nombreAprobador || "", firma: dataSat?.firmaAprobador || null },
          ])]
        : [])
    ],
    images: {
      logo: empresa.logo.base64Completa,
    },
    styles: {
      header: { fontSize: 9, bold: true },
      subheader: { fontSize: 8, bold: true },
      tableExample: { margin: [0, 5, 0, 5] },
      tablaSinMargen: { margin: [0, 0, 0, 0] },
      tableHeaderIzquierda: { bold: true, fontSize: 18, color: 'black' },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: 'black',
        alignment: 'center'
      },
      textoChico: { fontSize: 8 },
      textoChicoData: { fontSize: 6 },
      tituloTabla: {
        fontSize: 8,
        bold: true,
        fillColor: '#cccccc',
        style: 'subheader',
        alignment: 'center',
        border: true
      },
      textoTabla: { fontSize: 8, italics: false },
      textoTablaChico: { fontSize: 6, italics: false },
      totalChico: {
        fontSize: 6,
        bold: true,
        fillColor: '#cccccc',
        alignment: 'right',
        border: true
      },
      descuentoPartida: {
        fontSize: 5,
        color: "#777",
      }
    },
    pageSize: 'LETTER'
  };
  return dd;
};

export default ordenCompraPdf;
